import React from 'react';
import {
  Box,
  Container,
  Link,
  IconButton,
  Divider,
  useTheme,
  useMediaQuery,
  Typography,
} from '@mui/material';
import {
  Email as EmailIcon,
  LinkedIn as LinkedInIcon,
  GitHub as GitHubIcon,
  Twitter as TwitterIcon,
  Copyright as CopyrightIcon,
} from '@mui/icons-material';
import DownloadIcon from '@mui/icons-material/Download';
import { motion } from 'framer-motion';

const Footer: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isDark = theme.palette.mode === 'dark';

  const currentYear = new Date().getFullYear();

  const socialLinks = [
    {
      icon: <LinkedInIcon />,
      label: 'LinkedIn',
      url: 'https://linkedin.com/in/priyakant-charokar-enterprise-architect',
      color: 'inherit',
    },
    {
      icon: <GitHubIcon />,
      label: 'GitHub',
      url: 'https://github.com/priyakantcharokar/',
      color: 'inherit',
    },
    {
      icon: <TwitterIcon />,
      label: 'Twitter',
      url: 'https://x.com/Priyakant_459',
      color: 'inherit',
    },
    {
      icon: <EmailIcon />,
      label: 'Email',
      url: 'mailto:priyakantcharokar@gmail.com',
      color: 'inherit',
    },
    {
      icon: <DownloadIcon />,
      label: 'Download Resume',
      url: '/PriyakantCharokar.pdf',
      color: 'inherit',
    },
  ];

  return (
    <Box
      id="site-footer"
      component="footer"
      sx={{
        backgroundColor: isDark ? 'rgba(18, 18, 18, 0.95)' : 'rgba(248, 249, 250, 0.95)',
        borderTop: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
        py: { xs: 2, sm: 2.5, md: 3 },
        mt: 'auto',
      }}
    >
      <Container maxWidth="lg">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          {/* Social Links - Centered */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: { xs: 2, sm: 2.5, md: 3 } }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {socialLinks.map((social, index) => (
                <IconButton
                  key={index}
                  component={Link}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  size="large"
                  sx={{
                    color: theme.palette.text.primary,
                    backgroundColor: isDark ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0.05)',
                    border: isDark ? '1px solid rgba(0, 0, 0, 0.5)' : '1px solid transparent',
                    '&:hover': {
                      backgroundColor: isDark ? 'rgba(0, 0, 0, 0.5)' : 'rgba(0, 0, 0, 0.1)',
                      transform: 'translateY(-2px)',
                      border: isDark ? '1px solid rgba(0, 0, 0, 0.7)' : '1px solid transparent',
                    },
                    transition: 'all 0.3s ease',
                  }}
                  aria-label={social.label}
                >
                  {social.icon}
                </IconButton>
              ))}
            </Box>
          </Box>
        </motion.div>

        <Divider sx={{ my: 2, opacity: 0.3 }} />

        {/* Copyright Section */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          viewport={{ once: true }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CopyrightIcon sx={{ fontSize: 14, color: theme.palette.text.secondary }} />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ 
                  fontSize: { xs: '0.6875rem', sm: '0.75rem', md: '0.8rem' },
                  fontFamily: '"Roboto Mono", "Courier New", monospace',
                  lineHeight: { xs: 1.4, md: 1.5 },
                  '@media (min-width:601px) and (max-width:960px)': {
                    fontSize: '0.78125rem',
                  },
                }}
              >
                {currentYear} Priyakant charokar. All rights reserved.
              </Typography>
            </Box>
            
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ 
                fontSize: isMobile ? '0.75rem' : '0.8rem',
                fontFamily: '"Roboto Mono", "Courier New", monospace',
                '@media (min-width:601px) and (max-width:960px)': {
                  fontSize: '0.78125rem',
                },
              }}
            >
              Crafted with ❤️ and ✨ purpose to empower people through technology, leadership, and innovation.
            </Typography>
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default Footer; 